import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { documentsService, downloadBase64, fileToBase64, IDocumentItem, IDocumentType, IDocumentsFilter } from '../../../entities/documents'
import { useGlobalMessageActions } from '../../../entities/globalMessage'
import { MyError } from '../../../shared/lib/error/MyError'
import { Modal } from '../../../shared/ui/modal'
import { LoaderSpinner } from '../../../shared/ui/spinner'
import { Table } from '../../../shared/ui/table'
import { MyButton, MyButtonSecondary } from '../../../shared/ui/button'
import { Calendar } from '../../../shared/ui/calendar'
import financeClasses from '../../finance/ui/financeWidget.module.scss'
import workTgClasses from '../../workTgFilter/ui/workTgFilter.module.scss'
import { DropDownListSelected } from '../../../shared/ui/dropDownSelected'
import { IItem } from '../../../shared/model/types'
import { SurfaceBlock } from '../../../shared/ui/surfaceBlock'
import zipIcon from '../../../shared/lib/assets/zip.png'
import { STAFF_USER_ROUTE } from '../../../app/router/routes'
import { useAppSelector } from '../../../app/store/store'
import { FEATURES, hasFeature, hasFeatureGrant } from '../../../entities/my'
import classes from './documentsWidget.module.scss'

const USER_DOCUMENT_TYPE_LABELS: Record<string, string> = {
    USER_DOCUMENT_TYPE_GPH: 'ГПХ договор',
    USER_DOCUMENT_TYPE_TERMINATION: 'Договор о расторжении',
    USER_DOCUMENT_TYPE_ACT: 'Акт',
    USER_DOCUMENT_TYPE_TASK: 'Задание',
    USER_DOCUMENT_TYPE_RECEIPT: 'Чек',
    USER_DOCUMENT_TYPE_EMPLOYMENT_CONTRACT: 'Трудовой договор',
}

export const USER_DOCUMENT_TYPES: IDocumentType[] = [
    'USER_DOCUMENT_TYPE_GPH', 'USER_DOCUMENT_TYPE_TERMINATION', 'USER_DOCUMENT_TYPE_ACT',
    'USER_DOCUMENT_TYPE_TASK', 'USER_DOCUMENT_TYPE_RECEIPT', 'USER_DOCUMENT_TYPE_EMPLOYMENT_CONTRACT',
].map(value => ({value, label: USER_DOCUMENT_TYPE_LABELS[value] || value}))

const EMPTY_TYPES: IDocumentType[] = []

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    ...USER_DOCUMENT_TYPE_LABELS,
    STUDENT_DOCUMENT_TYPE_UNSPECIFIED: 'Неизвестный тип',
    STUDENT_DOCUMENT_TYPE_ACT: 'Акт',
    STUDENT_DOCUMENT_TYPE_TASK: 'Задание',
}

interface IFilterProps {
    value: IDocumentsFilter;
    types: IDocumentType[];
    onChange: (filter: IDocumentsFilter) => void;
    allTime: boolean;
    onAllTimeChange: (allTime: boolean) => void;
    financeStyle?: boolean;
    onDownloadZip?: () => void;
    isZipLoading?: boolean;
    onUploadClick?: () => void;
}

const toIsoDate = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(date)
    const read = (type: string) => parts.find(part => part.type === type)?.value || ''
    return `${read('year')}-${read('month')}-${read('day')}`
}
const parseIsoDate = (value: string) => value ? new Date(`${value}T00:00:00+03:00`) : null

export const DocumentsFilter: FC<IFilterProps> = ({value, types, onChange, allTime, onAllTimeChange, financeStyle = false, onDownloadZip, isZipLoading = false, onUploadClick}) => {
    const toggleType = (type: string) => {
        onAllTimeChange(false)
        onChange({...value, types: value.types.includes(type)
            ? value.types.filter(item => item !== type)
            : [...value.types, type]})
    }
    const selectAllTime = () => {
        onChange({from: '', to: '', types: []})
        onAllTimeChange(true)
    }

    const typesSelector = <section className={classes.typeFilter}>
        <span>Типы документов</span>
        <section className={classes.types}>
            {types.map(type => <label key={type.value}><input type="checkbox" checked={value.types.includes(type.value)} onChange={() => toggleType(type.value)} /> {type.label}</label>)}
        </section>
    </section>
    const onTypeSelected = (type: IItem<string>) => (selected: boolean) => toggleType(type.id)
    const typeItems: IItem<string>[] = types.map(type => ({id: type.value, name: type.label}))
    const financeTypesSelector = <section className={workTgClasses.container}>
        <span className={workTgClasses.label}>Типы документов:</span>
        <section className={workTgClasses.selector}>
            <DropDownListSelected
                items={typeItems}
                selectedIdItems={value.types}
                onSelected={onTypeSelected}
                onSelectAll={types => {
                    onAllTimeChange(false)
                    onChange({...value, types})
                }}
                selectedCount
                isLoading={false}
                placeholder="Выберите типы"
                search
                searchPlaceholder="Поиск по типам"
                emptyText="Типы документов не найдены"
            />
        </section>
    </section>
    if(financeStyle){
        return <section className={financeClasses.topRow}>
            <section className={classes.calendarWithZip}>
                <section className={financeClasses.calendar}><Calendar selectedStartDate={parseIsoDate(value.from)} selectedEndDate={parseIsoDate(value.to)} onDateRangeSelect={(from, to) => {
                    if(from || to) onAllTimeChange(false)
                    if(from && to) onChange({...value, from: toIsoDate(from), to: toIsoDate(to)})
                }} /></section>
                <button type="button" className={`${classes.allTime} ${allTime ? classes.allTimeActive : ''}`} onClick={selectAllTime}>За всё время</button>
                {onDownloadZip && <button
                    type="button"
                className={classes.zipDownload}
                aria-label="Скачать ZIP-архив"
                title="Скачать ZIP-архив"
                disabled={isZipLoading || (!allTime && (!value.from || !value.to))}
                    onClick={onDownloadZip}
                >
                    {isZipLoading ? <LoaderSpinner /> : <img src={zipIcon} alt="" />}
                </button>}
                {onUploadClick && <button
                    type="button"
                    className={classes.uploadDocument}
                    aria-label="Загрузить документ"
                    title="Загрузить документ"
                    onClick={onUploadClick}
                >
                    +
                </button>}
            </section>
            <section className={`${financeClasses.selectedWorkTgs} ${classes.financeTypes}`}>
                {financeTypesSelector}
            </section>
        </section>
    }
    return <section className={classes.filter}>
        <label>С <input type="date" value={value.from} onChange={event => { onAllTimeChange(false); onChange({...value, from: event.target.value}) }} /></label>
        <label>По <input type="date" value={value.to} onChange={event => { onAllTimeChange(false); onChange({...value, to: event.target.value}) }} /></label>
        <button type="button" className={`${classes.allTime} ${allTime ? classes.allTimeActive : ''}`} onClick={selectAllTime}>За всё время</button>
        {typesSelector}
    </section>
}

interface IUploadProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    types: IDocumentType[];
    onUpload: (file: File, type: string, uploadAt: string) => Promise<void>;
}

export const DocumentUploadModal: FC<IUploadProps> = ({open, setOpen, types, onUpload}) => {
    const [file, setFile] = useState<File | null>(null)
    const [type, setType] = useState('')
    const [uploadAt, setUploadAt] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [isTypeHintOpen, setIsTypeHintOpen] = useState(false)

    useEffect(() => {
        if(open){
            setFile(null); setType(''); setUploadAt(''); setError(''); setIsTypeHintOpen(false)
        }
    }, [open])

    const submit = async () => {
        if(!file || !type || !uploadAt){ setError('Выберите файл, тип документа и дату'); return }
        try { setLoading(true); setError(''); await onUpload(file, type, uploadAt); setOpen(false) }
        catch(error) { setError(error instanceof Error ? error.message : 'Не удалось загрузить документ') }
        finally { setLoading(false) }
    }

    return <Modal open={open} setOpen={setOpen}><section className={classes.modal}>
        <section className={classes.modalHeader}>
            <h3 className={classes.modalTitle}>Загрузить документ</h3>
            <button type="button" className={classes.modalClose} aria-label="Закрыть" title="Закрыть" onClick={() => setOpen(false)}>×</button>
        </section>
        <label>Файл <input type="file" onChange={(event: ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null)} /></label>
        <label>
            <section className={classes.documentTypeLabel}>
                <span>Тип документа</span>
                <section className={classes.documentTypeHint}>
                    <button
                        type="button"
                        className={classes.documentTypeHintButton}
                        aria-label="Подсказка по типам документов"
                        onMouseEnter={() => setIsTypeHintOpen(true)}
                        onMouseLeave={() => setIsTypeHintOpen(false)}
                    >?</button>
                    {isTypeHintOpen && <section className={classes.documentTypeHintPopup} role="tooltip">
                        <p><strong>Задание</strong> — на будущую неделю</p>
                        <p><strong>Акт</strong> — на прошлую неделю</p>
                    </section>}
                </section>
            </section>
            <DropDownListSelected
                items={types.map(item => ({id: item.value, name: item.label}))}
                selectedIdItems={type ? [type] : []}
                onSelected={item => () => setType(item.id)}
                isLoading={false}
                placeholder="Выберите тип документа"
            />
        </label>
        <label>Дата документа <input type="date" value={uploadAt} onChange={event => setUploadAt(event.target.value)} /></label>
        {error && <p className={classes.error}>{error}</p>}
        <section className={classes.modalActions}><MyButton isLoading={loading} onClick={submit}>Загрузить документ</MyButton></section>
    </section></Modal>
}

interface IAdapter {
    getTypes?: () => Promise<IDocumentType[]>;
    list: (filter?: IDocumentsFilter) => Promise<IDocumentItem[]>;
    upload?: (file: File, type: string, uploadAt: string) => Promise<void>;
    download?: (item: IDocumentItem) => Promise<void>;
    downloadZip: (filter?: IDocumentsFilter) => Promise<void>;
    remove?: (item: IDocumentItem) => Promise<void>;
}

interface IProps {
    title?: string;
    adapter: IAdapter;
    types?: IDocumentType[];
    canEdit?: boolean | ((item: IDocumentItem) => boolean);
    preset?: IDocumentsFilter & {key?: string | number};
    showUserName?: boolean;
    financeStyle?: boolean;
}

const formatDate = (value: string) => {
    if(!value) return '—'
    const date = new Date(value.length === 10 ? `${value}T00:00:00` : value)
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('ru-RU', {dateStyle: 'medium'}).format(date)
}

export const DocumentsWidget: FC<IProps> = ({title, adapter, types: initialTypes = EMPTY_TYPES, canEdit = false, preset, showUserName = false, financeStyle = false}) => {
    const {setGlobalMessage} = useGlobalMessageActions()
    const [types, setTypes] = useState(initialTypes)
    const [filter, setFilter] = useState<IDocumentsFilter>({from: '', to: '', types: []})
    const [allTime, setAllTime] = useState(false)
    const [items, setItems] = useState<IDocumentItem[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [hasLoadedList, setHasLoadedList] = useState(false)
    const [uploadOpen, setUploadOpen] = useState(false)
    const [deleteItem, setDeleteItem] = useState<IDocumentItem | null>(null)
    const [actionLoading, setActionLoading] = useState<string | null>(null)
    const [canUseEditActions, setCanUseEditActions] = useState(typeof canEdit === 'function' ? true : canEdit)

    useEffect(() => setCanUseEditActions(typeof canEdit === 'function' ? true : canEdit), [canEdit])
    useEffect(() => {
        if(preset){
            setAllTime(false)
            setFilter({from: preset.from, to: preset.to, types: preset.types})
        }
    }, [preset])
    useEffect(() => { setTypes(initialTypes) }, [initialTypes])
    useEffect(() => {
        if(!adapter.getTypes) return
        adapter.getTypes().then(received => { if(received.length) setTypes(received) }).catch(error => {
            if(error instanceof MyError && error.status === 403) setCanUseEditActions(false)
        })
    }, [adapter])

    const load = useCallback(async () => {
        if(!allTime && (!filter.from || !filter.to)){
            setItems([])
            setError('')
            setLoading(false)
            setHasLoadedList(false)
            return
        }
        try {
            setLoading(true)
            setError('')
            setItems(await adapter.list(allTime ? undefined : filter))
            setHasLoadedList(true)
        }
        catch(error) {
            setHasLoadedList(false)
            setError(error instanceof Error ? error.message : 'Не удалось получить документы')
        }
        finally { setLoading(false) }
    }, [adapter, allTime, filter])
    useEffect(() => { load() }, [load])

    const handleActionError = (error: unknown, fallback: string) => {
        if(error instanceof MyError && error.status === 403){
            setCanUseEditActions(false)
            setGlobalMessage({message: 'Нет прав на выполнение действия', type: 'error'})
            return
        }
        setGlobalMessage({message: error instanceof Error ? error.message : fallback, type: 'error'})
    }

    const upload = async (file: File, type: string, uploadAt: string) => {
        if(!adapter.upload) return
        try { await adapter.upload(file, type, uploadAt); await load(); setGlobalMessage({message: 'Документ загружен', type: 'ok'}) }
        catch(error) { handleActionError(error, 'Не удалось загрузить документ'); throw error }
    }

    const download = async (item: IDocumentItem) => {
        if(!adapter.download) return
        try { setActionLoading(`download-${item.id}`); await adapter.download(item) }
        catch(error) { handleActionError(error, 'Не удалось скачать файл') }
        finally { setActionLoading(null) }
    }
    const downloadZip = async () => {
        if(!allTime && (!filter.from || !filter.to)) return
        try { setActionLoading('zip'); await adapter.downloadZip(allTime ? undefined : filter) }
        catch(error) { handleActionError(error, 'Не удалось подготовить ZIP-архив') }
        finally { setActionLoading(null) }
    }
    const remove = async () => {
        if(!deleteItem || !adapter.remove) return
        try { setActionLoading(`delete-${deleteItem.id}`); await adapter.remove(deleteItem); setDeleteItem(null); await load(); setGlobalMessage({message: 'Документ удалён', type: 'ok'}) }
        catch(error) { handleActionError(error, 'Не удалось удалить документ') }
        finally { setActionLoading(null) }
    }
    const typeLabel = useMemo(() => new Map(types.map(type => [type.value, type.label])), [types])
    const showRowActions = Boolean(adapter.download || (canUseEditActions && adapter.remove))
    const canEditItem = (item: IDocumentItem) => canUseEditActions && (typeof canEdit === 'function' ? canEdit(item) : true)

    return <section className={classes.widget}>
        {title && <section className={classes.financeTitle}>{title}</section>}
        <section className={financeStyle ? classes.financeFilter : undefined}>
            <DocumentsFilter value={filter} onChange={setFilter} allTime={allTime} onAllTimeChange={setAllTime} types={types} financeStyle={financeStyle} onDownloadZip={downloadZip} isZipLoading={actionLoading === 'zip'} onUploadClick={canUseEditActions && adapter.upload ? () => setUploadOpen(true) : undefined} />
        </section>
        {!financeStyle && <section className={classes.actions}>
            {canUseEditActions && adapter.upload && <MyButtonSecondary onClick={() => setUploadOpen(true)}>Загрузить документ</MyButtonSecondary>}
            {!financeStyle && <MyButton disabled={!allTime && (!filter.from || !filter.to)} isLoading={actionLoading === 'zip'} onClick={downloadZip}>Скачать ZIP</MyButton>}
        </section>}
        {loading ? <section className={classes.loader}><LoaderSpinner /></section> : error ? <section className={classes.error}><p>{error}</p><MyButtonSecondary onClick={load}>Повторить</MyButtonSecondary></section> : items.length > 0 ? <section className={classes.tableWrap}><Table
            titles={[...(showUserName ? ['Сотрудник'] : []), 'Тип', 'Имя файла', 'Дата документа', 'Создан', ...(showRowActions ? ['Действия'] : [])]}
            rows={items.map(item => [
                ...(showUserName ? [item.user_id
                    ? <Link className={classes.staffLink} to={STAFF_USER_ROUTE.path.replace(':user_id', String(item.user_id))}>{item.user_name || `Сотрудник ${item.user_id}`}</Link>
                    : item.user_name || '—'] : []),
                typeLabel.get(item.type) || DOCUMENT_TYPE_LABELS[item.type] || item.type,
                adapter.download && (!showUserName || item.user_id) ? <a
                    href="#download"
                    className={classes.fileNameDownload}
                    onClick={event => { event.preventDefault(); download(item) }}
                    aria-label={`Скачать ${item.file_name}`}
                    title={`Скачать ${item.file_name}`}
                >{item.file_name}</a> : item.file_name,
                formatDate(item.upload_at), formatDate(item.created_at),
                ...(showRowActions ? [<section className={classes.rowActions}>
                    {adapter.download && (!showUserName || item.user_id) && <button
                        type="button"
                        className={`${classes.rowAction} ${classes.downloadAction}`}
                        disabled={actionLoading === `download-${item.id}`}
                        onClick={() => download(item)}
                        title="Скачать"
                        aria-label={`Скачать ${item.file_name}`}
                    >
                        {actionLoading === `download-${item.id}` ? <LoaderSpinner /> : <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 3V15" />
                            <path d="M7 10L12 15L17 10" />
                            <path d="M4 20H20" />
                        </svg>}
                    </button>}
                    {canEditItem(item) && adapter.remove && item.type !== 'USER_DOCUMENT_TYPE_RECEIPT' && <button
                        type="button"
                        className={`${classes.rowAction} ${classes.removeAction}`}
                        disabled={Boolean(actionLoading)}
                        onClick={() => setDeleteItem(item)}
                        title="Удалить"
                        aria-label={`Удалить ${item.file_name}`}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M4 7H20" />
                            <path d="M10 11V17M14 11V17" />
                            <path d="M6 7L7 20H17L18 7" />
                            <path d="M9 7V4H15V7" />
                        </svg>
                    </button>}
                </section>] : []),
            ])}
        /></section> : hasLoadedList && <section className={classes.empty}>Документы не найдены</section>}
        {adapter.upload && <DocumentUploadModal open={uploadOpen} setOpen={setUploadOpen} types={types} onUpload={upload} />}
        <Modal open={Boolean(deleteItem)} setOpen={() => setDeleteItem(null)}><section className={classes.modal}>
            <section className={classes.modalHeader}>
                <h3 className={classes.modalTitle}>Удалить документ?</h3>
                <button type="button" className={classes.modalClose} aria-label="Закрыть" title="Закрыть" onClick={() => setDeleteItem(null)}>×</button>
            </section>
            <p>Файл «{deleteItem?.file_name}» будет удалён без возможности восстановления.</p>
            <section className={classes.modalActions}><MyButton isLoading={actionLoading?.startsWith('delete-')} onClick={remove}>Удалить</MyButton></section>
        </section></Modal>
    </section>
}

export const UserDocumentsWidget: FC<{userId: number; canEdit: boolean; hideTitle?: boolean}> = ({userId, canEdit, hideTitle = false}) => {
    const adapter = useMemo<IAdapter>(() => ({
        getTypes: () => documentsService.getUserTypes(userId),
        list: filter => documentsService.listUser(userId, filter),
        upload: async (file, type, uploadAt) => documentsService.uploadUser(userId, {type, file_name: file.name, content_type: file.type || 'application/octet-stream', content: await fileToBase64(file), upload_at: uploadAt}),
        download: async item => downloadBase64(await documentsService.downloadUser(userId, item.id)),
        downloadZip: async filter => downloadBase64(await documentsService.downloadUserZip(userId, filter)),
        remove: item => documentsService.deleteUser(userId, item.id),
    }), [userId])
    return <DocumentsWidget title={hideTitle ? undefined : 'Документы сотрудника'} adapter={adapter} canEdit={canEdit} financeStyle />
}

export const StudentDocumentsWidget: FC<{studentId: number; canEdit: boolean; preset?: IDocumentsFilter & {key?: string | number}; hideTitle?: boolean}> = ({studentId, canEdit, preset, hideTitle = false}) => {
    const adapter = useMemo<IAdapter>(() => ({
        getTypes: () => documentsService.getStudentTypes(studentId),
        list: filter => documentsService.listStudent(studentId, filter),
        upload: async (file, type, uploadAt) => documentsService.uploadStudent(studentId, {type, file_name: file.name, content_type: file.type || 'application/octet-stream', content: await fileToBase64(file), upload_at: uploadAt}),
        download: async item => downloadBase64(await documentsService.downloadStudent(studentId, item.id)),
        downloadZip: async filter => downloadBase64(await documentsService.downloadStudentZip(studentId, filter)),
        remove: item => documentsService.deleteStudent(studentId, item.id),
    }), [studentId])
    return <DocumentsWidget title={hideTitle ? undefined : 'Документы ученика'} adapter={adapter} canEdit={canEdit} preset={preset} financeStyle />
}

export const UsersDocumentsRegistry: FC<{embedded?: boolean}> = ({embedded = false}) => {
    const {my} = useAppSelector(state => state.myReducer)
    const canViewDocuments = hasFeatureGrant(my, FEATURES.DOCUMENTS.VIEW)
    const adapter = useMemo<IAdapter>(() => ({
        list: filter => documentsService.listUsers(filter),
        download: async item => {
            if(!item.user_id) throw new Error('Не указан сотрудник документа')
            downloadBase64(await documentsService.downloadUser(item.user_id, item.id))
        },
        downloadZip: async filter => downloadBase64(await documentsService.downloadUsersZip(filter)),
        remove: async item => {
            if(!item.user_id) throw new Error('Не указан сотрудник документа')
            await documentsService.deleteUser(item.user_id, item.id)
        },
    }), [])
    const canEdit = (item: IDocumentItem) => Boolean(item.user_id)
        && hasFeature(my, FEATURES.DOCUMENTS.ADD, item.user_id)
    const widget = <DocumentsWidget title={embedded ? undefined : 'Документы сотрудников'} adapter={adapter} types={USER_DOCUMENT_TYPES} canEdit={canEdit} showUserName financeStyle />
    if(!canViewDocuments) return null
    return embedded ? widget : <SurfaceBlock className={classes.registrySurface}>{widget}</SurfaceBlock>
}
